import React, { useEffect, useState } from 'react';
import { Checkbox, Spinner } from '@bnvsrnd/brand-assets';
import api from '../../utils/api';

/**
 * 이용약관 + 개인정보처리방침 동의 체크박스 2개.
 * `group`에 따라 서버(GET /api/policies/:group)에서 문구를 받아와 보여준다 —
 * 텍스트를 프론트에 다시 옮겨적지 않고 server/constants/policies.js를 단일 출처로 쓴다.
 *
 * SignUp.js에는 폼 안에 그대로 삽입하고, 로그인/게스트 입장처럼 "이미 제출한 뒤"
 * 동의가 빠졌다고 판명나는 경우에는 ConsentModal이 이 컴포넌트를 감싸서 보여준다.
 */
function PolicyAgreement({ group, checked, onChange }) {
    const [policy, setPolicy] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expanded, setExpanded] = useState({ terms: false, privacy: false });

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        api.get(`/policies/${group}`)
            .then((res) => {
                if (!cancelled) setPolicy(res.data);
            })
            .catch(() => {
                if (!cancelled) setError('약관을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [group]);

    const toggleExpanded = (field) => {
        setExpanded((prev) => ({ ...prev, [field]: !prev[field] }));
    };

    if (loading) {
        return <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-4) 0' }}><Spinner size="sm" /></div>;
    }
    if (error || !policy) {
        return <p style={{ color: 'var(--status-danger)', fontSize: 13 }}>{error || '약관 정보를 찾을 수 없습니다.'}</p>;
    }

    const rows = [
        { field: 'terms', title: '(필수) 이용약관 동의', text: policy.terms },
        { field: 'privacy', title: '(필수) 개인정보처리방침 동의', text: policy.privacy },
        // 학생 그룹에만 있는 선택 항목 — 서비스 이용 필수 조건이 아니므로(개인정보 보호법 제22조)
        // 별도 체크박스로 받고, 제목 끝에 (선택)을 붙여 필수 항목과 구분한다.
        ...(policy.promotional ? [{ field: 'promotional', title: '결과물 홍보 활용 동의 (선택)', text: policy.promotional }] : [])
    ];

    // 전체 동의 — 화면에 보이는 항목(선택 항목 포함)을 한 번에 켜고 끈다.
    // 부모 onChange 는 모두 함수형 setState 라 항목별로 연달아 호출해도 안전하다.
    const fields = rows.map((r) => r.field);
    const allChecked = fields.every((f) => !!checked[f]);
    const toggleAll = (value) => fields.forEach((f) => onChange(f, value));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <div style={{
                border: '1px solid var(--border-default, #e0e0e0)',
                background: 'var(--surface-subtle, #f8f9fa)',
                borderRadius: 8,
                padding: 'var(--space-3)',
                fontWeight: 600
            }}>
                <Checkbox checked={allChecked} onChange={(e) => toggleAll(e.target.checked)}>
                    {policy.promotional ? '전체 동의 (선택 항목 포함)' : '전체 동의'}
                </Checkbox>
            </div>
            {rows.map(({ field, title, text }) => (
                <div key={field} style={{ border: '1px solid var(--border-default, #e0e0e0)', borderRadius: 8, padding: 'var(--space-3)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Checkbox
                            checked={!!checked[field]}
                            onChange={(e) => onChange(field, e.target.checked)}
                        >
                            {title}
                        </Checkbox>
                        <button
                            type="button"
                            onClick={() => toggleExpanded(field)}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: 'var(--text-secondary, #7a7f8a)',
                                fontSize: 13,
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                flexShrink: 0
                            }}
                        >
                            {expanded[field] ? '접기' : '전문보기'}
                        </button>
                    </div>
                    {expanded[field] && (
                        <div style={{
                            marginTop: 'var(--space-2)',
                            maxHeight: 200,
                            overflowY: 'auto',
                            whiteSpace: 'pre-wrap',
                            fontSize: 13,
                            lineHeight: 1.6,
                            color: 'var(--text-secondary, #4a4f57)',
                            background: 'var(--surface-subtle, #f8f9fa)',
                            padding: 'var(--space-3)',
                            borderRadius: 6
                        }}>
                            {text}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

export default PolicyAgreement;
